<template>
  <div class="conversations">
    <div class="cell">
      <div class="conversations__search">
        <div class="conversations__search-icon">
          <search-icon />
        </div>
        <input
          v-model.trim="searchQuery"
          class="conversations__search-input"
          type="text"
          :placeholder="$t('chat.search')"
        />
        <div class="conversations__search-badge">
          {{ conversationsCount }}
        </div>
      </div>
    </div>
    <div class="cell auto">
      <div class="conversations__block">
        <vue-scroll>
          <div class="conversations__list">
            <loader-conversation v-if="isConversationsLoading" />
            <template v-else>
              <conversation-empty
                v-if="isConversationsEmpty"
                :text="$t('chat.no-conversations')"
              />
              <template v-else>
                <conversation-empty
                  v-if="isResultsEmpty"
                  :text="$t('chat.no-result')"
                />
                <conversation-item
                  v-else
                  v-for="(conversation, i) in orderedConversations"
                  :key="`${i}-${conversation.client.id}`"
                  :conversation="conversation"
                  @clicked="markConversationAsSelected(conversation)"
                />
              </template>
            </template>
          </div>
        </vue-scroll>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import orderBy from 'lodash/orderBy';
import gql from 'graphql-tag';
import {
  APP_PLAY_SOUND,
  APP_SHOW_NOTIFICATION,
  CONVERSATION_LOAD_HISTORY,
  CONVERSATION_LOAD_MESSAGES,
  MESSAGES_LOADED,
} from '@/eventTypes';
import SearchIcon from '@/assets/icons/search-w.svg';
import listConversationsGql from '@/graphql/queries/listConversations.gql';
import newConversationGql from '@/graphql/suscriptions/newConversation.gql';
import setMessageGql from '@/graphql/suscriptions/setMessage.gql';
import removeConversationGql from '@/graphql/suscriptions/removeConversation.gql';
import getMessagesByClientIdGql from '@/graphql/queries/getMessagesByClientId.gql';
import lastConversationByClientIdGql from '@/graphql/queries/lastConversationByClientId.gql';
import EventBus from '@/eventBus';
import errorHandler from '@/utils/error.handler';

export default {
  components: {
    SearchIcon,
    ConversationEmpty: () => import('@/components/ConversationEmpty.vue'),
    ConversationItem: () => import('@/components/ConversationItem.vue'),
    LoaderConversation: () => import('@/components/loaders/Conversation.vue'),
  },
  props: {
    theme: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    searchQuery: '',
    listConversations: [],
    validMultimediaType: ['image', 'video', 'document'],
  }),
  computed: {
    ...mapState({
      conversations: (state) => state.conversations,
      activeClientId: (state) => state.activeClientId,
      isVisible: (state) => state.appIsVisible,
      isReady: (state) => state.ready,
    }),
    ...mapGetters(['userInfo', 'hasMultimedia', 'activeConversation']),
    campaignId() {
      return this.activeConversation
      && this.activeConversation.conversation
      && this.activeConversation.conversation.campaign
        ? this.activeConversation.conversation.campaign.id : null;
    },
    isConversationsLoading() {
      return this.$apollo.queries.listConversations.loading;
    },
    conversationsCount() {
      return this.conversations.length;
    },
    isConversationsEmpty() {
      return this.conversationsCount === 0;
    },
    filteredConversations() {
      if (this.searchQuery.length >= 3) {
        const q = this.normalizeString(this.searchQuery).toLowerCase();

        return this.conversations.filter((conversation) => {
          const { client } = conversation;

          return this.normalizeString(client.fullName).toLowerCase().indexOf(q) !== -1
            || this.normalizeString(client.name).toLowerCase().indexOf(q) !== -1
            || this.normalizeString(client.username).toLowerCase().indexOf(q) !== -1
            || (client.labels.length > 0 && this.findLabel(client.labels, q));
        });
      }

      return this.conversations;
    },
    isResultsEmpty() {
      return this.filteredConversations.length === 0;
    },
    orderedConversations() {
      return orderBy(this.filteredConversations, [(conversation) => {
        const lastMessage = conversation.messages.length > 0
          ? conversation.messages[conversation.messages.length - 1] : null;

        if (lastMessage) {
          return this.$moment(lastMessage.createdAt);
        }

        return null;
      }], ['desc']);
    },
    darkTheme() {
      return this.theme === 'dark';
    },
  },
  apollo: {
    listConversations() {
      return {
        query: gql`${listConversationsGql}`,
        skip() {
          return !this.isReady;
        },
        subscribeToMore: [
          {
            document: gql`${newConversationGql}`,
            updateQuery: (previousResult, { subscriptionData }) => {
              const { newMessage: newConversations } = subscriptionData.data;

              this.addConversations(newConversations);

              if (newConversations.length === 1) {
                const client = newConversations[0]?.client;
                const message = newConversations[0]?.messages[0];
                if (client && message) {
                  const title = client.name || client.fullName || this.$t('notification.new-conversation');
                  const body = this.buildNotificationBody(message);

                  EventBus.$emit(APP_SHOW_NOTIFICATION, title, body);
                  EventBus.$emit(APP_PLAY_SOUND);
                }
              }
              return {
                listConversations: newConversations,
              };
            },
          },
          {
            document: gql`${setMessageGql}`,
            updateQuery: (previousResult, { subscriptionData }) => {
              const { setMessage: message } = subscriptionData.data;
              const clientId = message.conversation.client;
              const isFromClient = message.typeUser === 'Client';
              const isNewMessage = message.readAt === null;
              const isFromCurrentConversation = clientId === this.activeClientId;

              if (isFromClient && isNewMessage && (!isFromCurrentConversation || !this.isVisible)) {
                EventBus.$emit(APP_PLAY_SOUND);
              }

              if (isFromClient && isNewMessage && !this.isVisible) {
                const title = message.user.name;
                const body = this.buildNotificationBody(message);

                EventBus.$emit(APP_SHOW_NOTIFICATION, title, body);
              }

              this.addMessageToConversation({
                clientId,
                message,
              });

              this.addMultimedia(message);
            },
          },
          {
            document: gql`${removeConversationGql}`,
            updateQuery: (previousResult, { subscriptionData }) => {
              const { removeConversation: data } = subscriptionData.data;
              this.removeConversation({
                clientId: data.clientId,
                channelId: data.channelId,
                action: data.action,
              });
            },
          },
        ],
      };
    },
  },
  created() {
    this.subscribeConversations();
  },
  activated() {
    this.subscribeConversations();
  },
  deactivated() {
    this.unsubscribeConversations();
  },
  destroyed() {
    this.unsubscribeConversations();
  },
  methods: {
    ...mapMutations([
      'addPreviousMessages',
      'setActiveClient',
      'addConversations',
      'addMessageToConversation',
      'setClientHistory',
      'removeConversation',
      'addUserMultimedia',
      'removeFromHistory',
    ]),
    ...mapMutations({
      setLoader: 'updateConversationLoadingState',
      setFirstLoad: 'updateConversationFirstLoadState',
      setTopReach: 'updateConversationTopReachedState',
    }),
    markConversationAsSelected(conversation) {
      this.setActiveClient(conversation.client.id);
      this.searchQuery = '';
    },
    async getMessages(clientId, lastId) {
      if (clientId.length !== 0) {
        this.setLoader({
          clientId,
          loading: true,
        });

        try {
          const { data: response } = await this.$apollo.query({
            query: getMessagesByClientIdGql,
            variables: {
              clientId,
              channelId: this.activeConversation.conversation.channel.id,
              lastId,
              length: 20,
            },
          });

          const messages = response.getMessages.data;

          this.addPreviousMessages({
            clientId,
            messages,
          });

          if (messages.length > 0) {
            this.setFirstLoad({
              clientId,
              firstLoad: false,
            });
          } else {
            this.setTopReach({
              clientId,
              topReached: true,
            });
          }
        } catch (e) {
          errorHandler.logErrors(e);
        } finally {
          this.setLoader({
            clientId,
            loading: false,
          });
        }

        EventBus.$emit(MESSAGES_LOADED, clientId);
      }
    },
    async loadClientHistory(clientId) {
      try {
        const { data: result } = await this.$apollo.query({
          query: lastConversationByClientIdGql,
          variables: {
            clientId,
            campaignId: this.campaignId,
          },
        });

        const history = result.lastConversationByClientId;

        this.setClientHistory({
          clientId,
          history,
        });
      } catch (e) {
        errorHandler.logErrors(e);
      }
    },
    normalizeString(str) {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    },
    addMultimedia(message) {
      const { type, multimedia, conversation } = message;
      const validMultimedia = !!multimedia && this.hasMultimedia(conversation.client, type);
      const validType = this.validMultimediaType.includes(type);

      if (validType && validMultimedia) {
        const newMultimedia = this.buildMultimediaMessage(message);
        this.addUserMultimedia(newMultimedia);
      }
    },
    subscribeConversations() {
      EventBus.$on(CONVERSATION_LOAD_MESSAGES, (clientId, messageId) => {
        if (messageId) {
          this.getMessages(clientId, messageId);
        }
      });

      EventBus.$on(CONVERSATION_LOAD_HISTORY, (clientId) => {
        if (this.campaignId) {
          this.loadClientHistory(clientId);
        }
      });
    },
    unsubscribeConversations() {
      EventBus.$off(CONVERSATION_LOAD_MESSAGES);
      EventBus.$off(CONVERSATION_LOAD_HISTORY);
    },
    findLabel(labels, text) {
      return labels.some((label) => this.normalizeString(label.value)
        .toLowerCase().indexOf(text) !== -1);
    },
  },
};
</script>
